import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, ArrowLeft, Loader2, Tag, ArrowUp } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import {
  cleanHtmlContent,
  getSEOTitle,
  getSEODescription,
  getSEOImage,
  getCanonicalUrl,
  WORDPRESS_SITE_URL,
} from '../services/wordpress';
import type { WordPressDraftPost } from '../types/wordpress';
import { getBlogPost } from '../services/blogApi';

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<WordPressDraftPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [slug]);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        if (!slug) {
          setLoading(false);
          return;
        }
        const mapped = await getBlogPost(slug);
        setPost(mapped);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Erro ao buscar post'));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  useEffect(() => {
    const onScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!post && loading) {
    return (
      <div className="bg-[#081428] text-white min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-400 mx-auto mb-4" />
            <p className="text-gray-400">Carregando post...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="bg-[#081428] text-white min-h-screen">
        <Navbar />
        <div className="container mx-auto max-w-4xl px-6 py-20">
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-4">Post não encontrado</h1>
            <p className="text-gray-400 mb-8">
              {error?.message || 'O post que você está procurando não existe ou foi removido.'}
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar para o blog
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const featuredImage =
    getSEOImage(post) ||
    (post.featured_image as any)?.urls?.large?.url ||
    (post.featured_image as any)?.urls?.full?.url ||
    (post.featured_image as any)?.urls?.medium?.url ||
    (post.featured_image as any)?.urls?.thumbnail?.url ||
    undefined;
  const tags: string[] = post.tags || [];
  const tocItems = post.toc?.items || [];
  
  // Formatar datas
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(date);
  };

  const formatDateISO = (dateString: string) => {
    return new Date(dateString).toISOString();
  };

  // Extrair dados de SEO (Rank Math PRO > Yoast > Padrão)
  const seoTitle = getSEOTitle(post);
  const seoDescription = getSEODescription(post);
  const seoImage = featuredImage || 'https://imagedelivery.net/xVIHcLAprA3z5a44SkpIlA/3c243362-9367-4613-ec3b-4eea68888c00/public';
  const canonicalUrl = getCanonicalUrl(post);
  
  const robots = post.rank_math?.final?.robots || '';
  const noindex = robots.includes('noindex');
  const nofollow = robots.includes('nofollow');
  const keywords = post.rank_math?.final?.focus_keyword || tags.join(', ');

  return (
    <div className="bg-[#081428] text-white min-h-screen">
      {post && (
        <SEO
          title={seoTitle}
          description={seoDescription}
          image={seoImage}
          url={
            canonicalUrl
              ? canonicalUrl.replace(WORDPRESS_SITE_URL || '', '')
              : `/blog/${post.slug}`
          }
          type="article"
          publishedTime={formatDateISO(post.modified_gmt || '')}
          modifiedTime={formatDateISO(post.modified_gmt || '')}
          author="LawX"
          section={undefined}
          tags={tags}
          keywords={keywords}
          noindex={noindex}
          nofollow={nofollow}
          facebookTitle={post.rank_math?.final?.title}
          facebookDescription={post.rank_math?.final?.description}
          facebookImage={post.rank_math?.final?.facebookImage}
          twitterTitle={post.rank_math?.final?.title}
          twitterDescription={post.rank_math?.final?.description}
          twitterImage={post.rank_math?.final?.twitterImage}
          canonicalUrl={post.rank_math?.final?.canonical}
          schemaData={post.rank_math?.final?.schema}
        />
      )}

      <Navbar />

      {/* Breadcrumbs */}
      <div className="pt-24 pb-6 px-6">
        <div className="container mx-auto max-w-4xl">
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-8">
            <Link to="/" className="hover:text-white transition-colors">
              Início
            </Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-white transition-colors">
              Blog
            </Link>
            <span>/</span>
            <span className="text-white">{post.title}</span>
          </nav>
        </div>
      </div>

      <article className="pb-16 px-6">
        <div className="container mx-auto max-w-4xl">
          {/* Botão voltar */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </button>

          {/* Título */}
          <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
            {post.title}
          </h1>

          {/* Meta informações */}
          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-400 mb-8 pb-8 border-b border-white/10">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <time dateTime={post.modified_gmt || ''}>
                {formatDate(post.modified_gmt || '')}
              </time>
            </div>
            <div className="text-xs text-gray-500">
              Atualizado em {formatDate(post.modified_gmt || '')}
            </div>
          </div>

          {/* Imagem destacada */}
          {featuredImage && (
            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={featuredImage}
                alt={post.title}
                className="w-full h-auto object-cover"
                loading="eager"
              />
            </div>
          )}

          {/* Sumário */}
          {tocItems.length > 0 && (
            <div className="mb-10 rounded-lg border border-white/10 bg-[#0a1a2e] p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Sumário</h2>
              <ul className="space-y-2 text-sm text-gray-300">
                {tocItems.map((item) => {
                  const href = item.anchor?.startsWith('#') ? item.anchor : `#${item.id}`;
                  return (
                    <li key={item.id} className={item.level === 3 ? 'ml-4' : ''}>
                      <a
                        href={href}
                        className="hover:text-white transition-colors"
                      >
                        {item.title}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Conteúdo do post */}
          <div
            className="post-content"
            dangerouslySetInnerHTML={{
              __html: cleanHtmlContent(post.content || ''),
            }}
          />

          {/* Tags */}
          {tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-white/10">
              <div className="flex flex-wrap items-center gap-3">
                <Tag className="w-5 h-5 text-gray-400" />
                <span className="text-gray-400 font-medium">Tags:</span>
                {tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1.5 text-sm bg-[#0a1a2e] border border-white/10 text-gray-300 rounded-lg hover:border-white/20 transition-colors"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Navegação */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Ver todos os posts
            </Link>
          </div>
        </div>
      </article>

      <Footer />

      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Voltar ao topo"
          className="fixed bottom-6 right-6 z-40 rounded-full bg-blue-600/90 p-3 text-white shadow-lg transition hover:bg-blue-600"
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

